import { ApiResponseError } from "../errors/api-response-error";
import useAxiosPrivateToken from "../hooks/usePrivateAxios";
import { TypeErrorCodeToMessageMap } from "../interface/API/apiError.interface";
import {
    CheckOtpCodeApiResponseType, CheckPermitValidationOtpApiResType,
    CheckRequestStatusOtpApiResponseType, PreUpdateVehicleOtpApiApiResType, SendOtpCodeApiResponseType,
    SendOtpCodeApiType
} from "../interface/API/homePageApi.interface";
import ResponseStatus from "../types/responseStatus.type";
import {
    isCheckOtpCodeApiResponseValid, isCheckRequestStatusOtpApiResponseValid,
    isCheckValidationPermitOtpApiResValid, isPreRenewedPermitOtpApiResValid, isPreUpdateVehicleOtpApiResValid, isSendOtpCodeApiResponseValid
} from "../validations/api/homePageApiValidations";
import {
    CHECK_OTP_CODE_API,
    CHECK_PERMIT_VALIDATION_DATE_API,
    CHECK_REQUEST_STATUS_API,
    PRE_RENEWED_PERMIT_OTP_API,
    PRE_UPDATE_VEHICLE_DETAILS_API,
    SEND_OTP_CODE_API,
} from "./axios-api";

interface ICheckOtpFormFApi {
    personId: string;
    vehicleNum: string;
    otpCode: string;
    isPermit: boolean;
}

type PreRenewedPermitFormOtpApi = {
    personId: string;
    vehicleNum: string;
    otpCode: string;
}

type PreRenewedPermitOtpApiRes = {
    isSuccess: boolean;
    endValidityDate: string;
    errorName: string;
    message: string;
    permitId: number;
    isCompany: boolean;
    permitFee: number;
}

export default function useOtpModalApi() {

    const { axiosLolatechPortalPrivate, handleError } = useAxiosPrivateToken();

    async function preRenewedPermitOtpApi(form: PreRenewedPermitFormOtpApi) {

        const result: PreRenewedPermitOtpApiRes = {
            isSuccess: true,
            endValidityDate: '',
            errorName: '',
            message: '',
            isCompany: false,
            permitId: -1,
            permitFee: 0
        }

        try {

            const response = await axiosLolatechPortalPrivate.post(PRE_RENEWED_PERMIT_OTP_API, form);
            const data = response.data;

            if (!isPreRenewedPermitOtpApiResValid(data)) {

                throw new ApiResponseError('Error validating pre renewed permit OTP API response');
            }

            result.endValidityDate = data.endValidityDate;
            result.isCompany = data.isCompany;
            result.permitFee = data.permitFee;
            result.permitId = data.permitId;

        } catch (error) {

            result.isSuccess = false;
            result.message = handleError(error);
        }

        return result;
    }

    async function preUpdateVehicleOtpApi(form: ICheckOtpFormFApi) {

        const result: PreUpdateVehicleOtpApiApiResType = {
            isSuccess: true,
            endValidityDate: '',
            errorName: '',
            message: ''
        }

        try {

            const response = await axiosLolatechPortalPrivate.post(PRE_UPDATE_VEHICLE_DETAILS_API, form);
            const data = response.data;

            if (!isPreUpdateVehicleOtpApiResValid(data)) {

                throw new ApiResponseError('Error validating pre update vehicle OTP API response');
            }

            result.endValidityDate = data.endValidityDate;

        } catch (error) {

            result.isSuccess = false;
            result.message = handleError(error);
        }

        return result;
    }

    async function sendOtpCodeApi(formData: SendOtpCodeApiType): Promise<SendOtpCodeApiResponseType> {

        const result: SendOtpCodeApiResponseType = {
            isSuccess: true,
            errorName: '',
            message: '',
            mobileOrEmailForGUI: '',
            sendVia: "EMAIL"
        }
        console.log('sendOtpCodeApi called');

        try {

            const response = await axiosLolatechPortalPrivate.post(SEND_OTP_CODE_API, formData);

            const data = response.data;

            
            if (!isSendOtpCodeApiResponseValid(data)) {

                throw new ApiResponseError('Error validating send OTP code API response');
            }

            result.message = 'send OTP code API call successful';
            result.mobileOrEmailForGUI = data.mobileOrEmailForGUI;
            result.sendVia = data.sendVia;
        } catch (error: any) {

            let costumDefaultErrorMsg: TypeErrorCodeToMessageMap = {}

            if (formData.portalAction === 'RENEWED_PERMIT_OPTION') {
                costumDefaultErrorMsg = {
                    "PERMIT_NOT_FOUND": {
                        "Hebrew": { default: 'לא נמצא תו מתאים לחידוש' },
                        "Arabic": { default: 'لم يتم العثور على ملصق مناسب للتجديد' },
                        "English": { default: 'No suitable sticker found for renewal' }
                    }
                }
            }

            result.isSuccess = false;
            result.message = handleError(error, costumDefaultErrorMsg);
        }

        return result;
    }

    async function checkOtpCodeApi(form: ICheckOtpFormFApi): Promise<CheckOtpCodeApiResponseType> {

        const result: CheckOtpCodeApiResponseType = {
            isSuccess: true,
            isOtpCodeValid: true,
            errorName: '',
            message: ''
        }

        try {

            const response = await axiosLolatechPortalPrivate.post(CHECK_OTP_CODE_API, form);
            const data = response.data;

            if (!isCheckOtpCodeApiResponseValid(data) || data.status !== ResponseStatus.SUCCESS) {
                throw new ApiResponseError('Error validating check OTP code API response');
            }

            const isOtpCodeValid = data.data.isOtpCodeValid;

            if (isOtpCodeValid !== true) {

                throw new ApiResponseError('OTP code is not valid');
            }

            result.isOtpCodeValid = isOtpCodeValid;

        } catch (error) {

            result.isSuccess = false;
            result.message = handleError(error);
        }

        return result;
    }

    async function checkRequestStatusOtpApi(form: ICheckOtpFormFApi): Promise<CheckRequestStatusOtpApiResponseType> {

        const result: CheckRequestStatusOtpApiResponseType = {
            isSuccess: true,
            requestStatus: 1,
            errorName: '',
            message: ''
        }

        try {

            const response = await axiosLolatechPortalPrivate.post(CHECK_REQUEST_STATUS_API, form);
            const data = response.data;

            if (!isCheckRequestStatusOtpApiResponseValid(data)) {
                throw new ApiResponseError('Error validating check request status OTP API response');
            }

            result.requestStatus = data.data.requestStatus;

        } catch (error) {

            result.isSuccess = false;
            result.message = handleError(error);
        }

        return result;
    }

    async function checkPermitValidationDateOtpApi(form: ICheckOtpFormFApi): Promise<CheckPermitValidationOtpApiResType> {

        const result: CheckPermitValidationOtpApiResType = {
            isSuccess: true,
            dateValidationPeriod: '',
            errorName: '',
            message: ''
        }

        try {

            const response = await axiosLolatechPortalPrivate.post(CHECK_PERMIT_VALIDATION_DATE_API, form);
            const data = response.data;

            if (!isCheckValidationPermitOtpApiResValid(data)) {

                throw new ApiResponseError('Error validating check permit validation date OTP API response');
            }

            result.dateValidationPeriod = data.data.dateValidationPeriod;

        } catch (error) {

            result.isSuccess = false;
            result.message = handleError(error);
        }

        return result;
    }

    return {
        sendOtpCodeApi, checkOtpCodeApi,
        checkRequestStatusOtpApi, checkPermitValidationDateOtpApi,
        preRenewedPermitOtpApi, preUpdateVehicleOtpApi
    };
}